import { useRef, type ReactNode } from 'react';
import { CircleDot, Flag, Layers, Shapes, SlidersHorizontal, Tag, UserRound } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import type { FilterSpec, DisplaySpec } from '../../lib/filters';
import { BACKLOG_ORDER_OPTIONS, defaultWorkListOrderBy } from '../../lib/work-list-sort';
import {
  memberDisplayName,
  WORK_ITEM_TYPES,
  workItemTypeLabel,
  type WorkItemSubtypeDefinition,
} from '../../lib/work-types';
import ListFilterBar, { type ListFilterChip } from './ListFilterBar';
import ShowSubtasksCheckbox from './ShowSubtasksCheckbox';
import { useOverlays } from './overlay-context';
import { useSession } from '../session-context';
import { useProjectDecorations } from './use-project-decorations';

/** The plain statuses, each standing for one or more state groups. */
const STATUS_OPTIONS = [
  { value: 'backlog,unstarted', label: 'Not started' },
  { value: 'started', label: 'In progress' },
  { value: 'completed', label: 'Done' },
  { value: 'cancelled', label: 'Stopped' },
] as const;

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'none', label: 'None' },
] as const;

type Props = {
  filters: FilterSpec;
  display: DisplaySpec;
  onChange: (next: { filters: FilterSpec; display: DisplaySpec }) => void;
  onClear: () => void;
  projectId?: string;
  /** When set, type is owned by the page menu and is not a filter chip. */
  lockedType?: string | null;
  /** State groups that belong to the page, not to a user-applied filter. */
  defaultStateGroups?: readonly string[];
  /** Extra controls for the layout in use, placed with the other display controls. */
  trailing?: ReactNode;
  /** Cross-stream views can supply the union of their stream subtype definitions. */
  subtypeOptions?: WorkItemSubtypeDefinition[];
};

function toggleValue(current: string[], value: string): string[] {
  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
}

function toggleNullable(current: (string | null)[], value: string | null): (string | null)[] {
  return current.some((item) => item === value)
    ? current.filter((item) => item !== value)
    : [...current, value];
}

export default function FilterBar({
  filters,
  display,
  onChange,
  onClear,
  projectId,
  lockedType,
  trailing,
  subtypeOptions,
}: Props) {
  const { pushBlock, popBlock } = useOverlays();
  const { userId } = useSession();
  const { labels, members, subtypes } = useProjectDecorations(projectId);
  const availableSubtypes = subtypeOptions ?? subtypes;
  const selectedStates = filters.state_groups ?? [];
  const selectedPriorities = filters.priorities ?? [];
  const selectedAssignees = filters.assignee_ids ?? [];
  const selectedLabels = filters.label_ids ?? [];
  const selectedTypes = filters.types ?? [];
  const selectedSubtypes = filters.subtypes ?? [];
  const hasBugSubtype = availableSubtypes.some((subtype) => subtype.key === 'bug');
  const latest = useRef({ filters, display, onChange });
  latest.current = { filters, display, onChange };

  const selectedStatuses = STATUS_OPTIONS.filter((option) =>
    option.value.split(',').every((group) => selectedStates.includes(group)),
  ).map((option) => option.value);
  const mine = Boolean(userId) && selectedAssignees.length === 1 && selectedAssignees[0] === userId;
  const chips: ListFilterChip[] = [
    {
      id: 'state',
      label: 'Status',
      icon: CircleDot,
      options: [...STATUS_OPTIONS],
      selected: selectedStatuses,
      onToggle: (value) => {
        const groups = value.split(',');
        const on = groups.every((group) => selectedStates.includes(group));
        const next = on
          ? selectedStates.filter((group) => !groups.includes(group))
          : Array.from(new Set([...selectedStates, ...groups]));
        onChange({
          filters: { ...filters, state_groups: next.length ? next : null },
          display,
        });
      },
    },
    {
      id: 'priority',
      label: 'Priority',
      icon: Flag,
      options: [...PRIORITY_OPTIONS],
      selected: selectedPriorities,
      onToggle: (value) => {
        const next = toggleValue(selectedPriorities, value);
        onChange({
          filters: { ...filters, priorities: next.length ? next : null },
          display,
        });
      },
    },
  ];
  if (!lockedType) {
    chips.push({
      id: 'type',
      label: 'Type',
      icon: Layers,
      options: WORK_ITEM_TYPES.map((type) => ({ value: type, label: workItemTypeLabel(type) })),
      selected: selectedTypes,
      onToggle: (value) => {
        const next = toggleValue(selectedTypes, value);
        onChange({
          filters: { ...filters, types: next.length ? next : null },
          display,
        });
      },
    });
  }
  if (availableSubtypes.length > 0 || selectedSubtypes.length > 0) {
    chips.push({
      id: 'subtype',
      label: lockedType ? 'Type' : 'Task type',
      icon: Shapes,
      options: availableSubtypes.map((subtype) => ({
        value: subtype.key,
        label: subtype.name,
      })),
      selected: selectedSubtypes,
      onToggle: (value) => {
        const next = toggleValue(selectedSubtypes, value);
        onChange({
          filters: { ...filters, subtypes: next.length ? next : null },
          display,
        });
      },
    });
  }
  const moreChips: ListFilterChip[] = [];
  if (members.length > 0) {
    moreChips.push({
      id: 'assignee',
      label: 'Assignee',
      icon: UserRound,
      options: members.map((member) => ({ value: member.user_id, label: memberDisplayName(member) })),
      selected: selectedAssignees.filter((id): id is string => id !== null),
      onToggle: (value) => {
        const next = toggleNullable(selectedAssignees, value);
        onChange({
          filters: { ...filters, assignee_ids: next.length ? next : null },
          display,
        });
      },
    });
  }
  if (projectId) {
    moreChips.push({
      id: 'label',
      label: 'Label',
      icon: Tag,
      options: labels.map((label) => ({ value: label.id, label: label.name })),
      selected: selectedLabels.filter((id): id is string => id !== null),
      onToggle: (value) => {
        const next = toggleNullable(selectedLabels, value);
        onChange({
          filters: { ...filters, label_ids: next.length ? next : null },
          display,
        });
      },
    });
  }

  return (
    <ListFilterBar
      query={filters.query ?? null}
      onQueryChange={(nextQuery) => {
        const { filters: nextFilters, display: nextDisplay, onChange: commit } = latest.current;
        commit({
          filters: { ...nextFilters, query: nextQuery },
          display: nextDisplay,
        });
      }}
      searchPlaceholder={
        lockedType === 'incident' ? 'Search incidents...' : lockedType === 'problem' ? 'Search problems...' : 'Search tasks...'
      }
      searchAriaLabel="Search work items"
      quick={
        userId
          ? [
              {
                id: 'mine',
                label: 'Mine',
                active: mine,
                onToggle: () =>
                  onChange({
                    filters: { ...filters, assignee_ids: mine ? null : [userId] },
                    display,
                  }),
              },
            ]
          : []
      }
      chips={chips}
      moreChips={moreChips}
      afterChips={
        <>
          <DropdownMenu
            onOpenChange={(open) => {
              if (open) pushBlock();
              else popBlock();
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 px-2 font-normal text-muted-foreground">
                <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                Display
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuCheckboxItem
                checked={display.show_empty_groups !== false}
                onCheckedChange={(checked) =>
                  onChange({
                    filters,
                    display: { ...display, show_empty_groups: checked === true },
                  })
                }
              >
                Show empty groups
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ShowSubtasksCheckbox
            id="filter-show-subtasks"
            checked={display.show_sub_items === true}
            onCheckedChange={(checked) =>
              onChange({
                filters,
                display: { ...display, show_sub_items: checked },
              })
            }
          />
          {hasBugSubtype && (!lockedType || lockedType === 'task') ? (
            <ShowSubtasksCheckbox
              id="filter-show-bugs"
              label="Show bugs"
              checked={display.show_bugs !== false}
              onCheckedChange={(checked) =>
                onChange({
                  filters,
                  display: { ...display, show_bugs: checked },
                })
              }
            />
          ) : null}
        </>
      }
      sort={{
        value: defaultWorkListOrderBy(display.layout, display.order_by),
        options: [...BACKLOG_ORDER_OPTIONS],
        onChange: (value) => onChange({ filters, display: { ...display, order_by: value } }),
      }}
      trailing={trailing}
      onClear={onClear}
    />
  );
}
